// i18next-extract-mark-ns-start privacy-policy

import { AnchorLink } from 'components/AnchorLink';
import {Article} from 'components/Article';
import { InternalPageLink } from 'components/links/Pages';
import {SEO} from 'components/SEO';
import {graphql} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';
import React from 'react';

const Page: React.FC = () => {
  const {t} = useI18next();
  return (
    <Article wide>
      <SEO
        path="privacy-policy"
        title={t('Privacy Policy')}
        description={t(
          'This policy explains the what, how, and why of the information we collect when you visit one of our websites, or when you use our Services. '
        )}
      />
      <Trans parent="h1">Privacy Policy</Trans>
      <Trans parent="p">
        The data controller of your personal data is MONEI DIGITAL PAYMENTS S.L. (hereinafter,
        "MONEI"), with registered office at Calle Palestina, 1, Entreplanta, 29007, Málaga, Spain,
        which offers its services to users (hereinafter, the "Users") through the website{' '}
        <InternalPageLink to="/">
          monei.com
        </InternalPageLink>{' '}
        (hereinafter, the "Website") and the MONEI Pay mobile application (hereinafter, the "Application"
        or "App").
      </Trans>
      <Trans parent="p">
        For any questions or requests regarding data protection, you can contact us through the
        following email address:{' '}
        <AnchorLink href="mailto:support@monei.com">
          support@monei.com
        </AnchorLink>
      </Trans>
      <Trans parent="p">
        For more information about the services offered, you can consult our{' '}
        <InternalPageLink to="/legal-notice">
          Terms and Conditions of Service.
        </InternalPageLink>
      </Trans>
      <Trans parent="p">
        If you want to learn more about our affiliate program, you can consult our{' '}
        <AnchorLink href="https://support.monei.com/hc/en-us/articles/360020603817-MONEI-Affiliate-Program-Terms-and-Conditions">
          Affiliate Program Terms and Conditions.
        </AnchorLink>
      </Trans>
      <div>
        <Trans parent="h2">Data Subject to Processing</Trans>
      </div>
      <Trans parent="p">
        MONEI may process the following categories of personal data provided by Users through the
        Website or App:
      </Trans>
      <ul>
        <Trans parent="li">
          Identification and contact data (name, surname, ID number, phone number, address, email).
        </Trans>
        <Trans parent="li">
          Information about your company (Tax ID, address, business sector, turnover)
        </Trans>
        <Trans parent="li">
          Data related to the use of the Website and App (navigation information and cookies).
        </Trans>
      </ul>
      <div>
        <Trans parent="h2">Purposes of Personal Data Processing</Trans>
      </div>
      <div>
        <Trans parent="h3">Contact</Trans>
      </div>
      <Trans parent="p">
        If you have contacted us through the various available means (contact form, social networks,
        email, or phone), MONEI will process your personal data (identification and contact data or
        any information you provide in your request) to contact you, attend to and manage your
        request based on the consent provided through the active submission of the request.
      </Trans>
      <div>
        <Trans parent="h3">Service Provision</Trans>
      </div>
      <Trans parent="p">
        In the case of users who acquire products or services offered by MONEI, their personal data
        will be processed within the framework of the contractual relationship to manage the
        provision of services. You can obtain more information about this processing in the{' '}
        <InternalPageLink to="/protection-policy">
          Data Protection Policy for customers
        </InternalPageLink>{' '}
        (merchants and end users).
      </Trans>
      <div>
        <Trans parent="h3">Commercial Information</Trans>
      </div>
      <Trans parent="p">
        Only if you have not checked the opposition box or have subscribed to our newsletter or
        MONEI Blog, we will send you via email information and trends about payments, expert
        business advice, and news about MONEI.
      </Trans>
      <Trans parent="p">
        You can unsubscribe from the notification service at any time by sending an email to{' '}
        <AnchorLink href="mailto:support@monei.com">
          support@monei.com
        </AnchorLink>{' '}
        or by clicking on the link provided for this purpose in each communication.
      </Trans>
      <div>
        <Trans parent="h3">Cookies</Trans>
      </div>
      <Trans parent="p">
        MONEI uses cookies and other technologies to obtain analytical and statistical information
        about your navigation and use of our page as well as to provide you with personalized
        advertising.
      </Trans>
      <Trans parent="p">
        The installation of these cookies is done voluntarily by users, who can at any time
        configure their preferences or withdraw their consent.
      </Trans>
      <Trans parent="p">
        If you want to know more details about the cookies and other technologies we use, their
        purpose, and other information of interest, you can consult our{' '}
        <InternalPageLink to="/cookies-policy">
          Cookies Policy
        </InternalPageLink>
        .
      </Trans>
      <div>
        <Trans parent="h2">Retention Period</Trans>
      </div>
      <Trans parent="p">
        <strong>Contact:</strong> We will process your data for the time necessary to attend to and
        resolve the request you have made, and in any case within a maximum period of 1 year, MONEI
        will proceed to delete it unless its conservation is mandatory for the exercise or defense of
        claims or some type of judicial, legal, or contractual liability may arise from its
        processing. In these cases, we will keep the information properly blocked.
      </Trans>
      <Trans parent="p">
        Service Provision: In the case of users who acquire products or services offered by MONEI,
        their personal data will be retained for the periods established in the{' '}
        <InternalPageLink to="/protection-policy">
          Data Protection Policy for customers
        </InternalPageLink>{' '}
        (merchants and end users).
      </Trans>
      <Trans parent="p">
        Commercial Information: MONEI will process your personal data for sending commercial
        information until you request to unsubscribe from the notification service.
      </Trans>
      <Trans parent="p">
        <strong>Cookies:</strong> Personal data from cookies will be processed while you do not
        withdraw your consent and for their duration period.
      </Trans>
      <div>
        <Trans parent="h2">Data Communication</Trans>
      </div>
      <Trans parent="p">
        MONEI will not transfer your personal data to third parties. However, for the correct
        provision of services, MONEI may rely on third-party service providers who may have access
        to personal data as a result of the execution of contracted services (e.g., technology
        development and maintenance services). In any case, MONEI follows strict criteria in
        selecting these third parties to comply with its data protection obligations and signs the
        corresponding data protection contract with them, where these third parties are obliged to
        comply with their data protection obligations, and specifically, to comply with legal,
        technical and organizational measures, the processing of personal data for the agreed
        purposes, and the prohibition of processing such personal data for other purposes or
        transfer to third parties.
      </Trans>
      <div>
        <Trans parent="h2">International Data Transfers</Trans>
      </div>
      <Trans parent="p">No international data transfers are made.</Trans>
      <div>
        <Trans parent="h2">Data Protection Rights</Trans>
      </div>
      <Trans parent="p">
        You may exercise your rights of access, rectification, deletion, opposition, limitation, and
        portability by postal mail to Calle Palestina, 1, Entreplanta, 29007, Málaga, Spain or by
        email to{' '}
        <AnchorLink href="mailto:support@monei.com">
          support@monei.com
        </AnchorLink>
        , proving your identity.
      </Trans>
      <Trans parent="p">
        Likewise, you may file a complaint with the Spanish Data Protection Agency (AEPD), whose
        contact details are provided through the website{' '}
        <AnchorLink href="https://www.aepd.es">
          aepd.es
        </AnchorLink>
        .
      </Trans>
    </Article>
  );
};

export default Page;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: {ns: {in: ["common", "privacy-policy"]}, language: {eq: $language}}
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
